import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Zap, HardHat, Euro, ChevronRight, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';

/**
 * Widget Admin Full - Rappel des prospects sans commissions renseignées
 * 
 * Affiche les prospects qui sont avancés dans le process mais n'ont pas encore
 * de commissions totales renseignées :
 * - Easy Power : à partir de "Étude reçue" (ep_etude_recue)
 * - Chantier : à partir de "Devis accepté" (ch_devis_accepte)
 */
export function CommissionsPendingWidget() {
    const [loading, setLoading] = useState(true);
    const [prospects, setProspects] = useState([]);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadPendingCommissions();
    }, []);

    const loadPendingCommissions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/admin/prospects-without-commission');
            setProspects(response.data?.prospects || []);
        } catch (err) {
            console.error('Erreur chargement prospects sans commission:', err);
            setError('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    const getProspectName = (prospect) => {
        return prospect.company_name || `${prospect.first_name || ''} ${prospect.last_name || ''}`.trim() || 'Sans nom';
    };

    // Labels des étapes
    const STAGE_LABELS = {
        // Easy Power
        ep_etude_recue: 'Étude reçue',
        ep_etude_expliquee: 'Étude expliquée',
        ep_rib_receptionne: 'RIB réceptionné',
        ep_contrat_envoye: 'Contrat envoyé',
        ep_contrat_explique: 'Attente signature',
        ep_gagne: 'Gagné',
        ep_questionnaire_chantier: 'Form. chantier',
        // Chantier
        ch_devis_accepte: 'Devis accepté',
        ch_planification: 'Planification',
        ch_en_cours: 'En cours',
        ch_termine: 'Terminé',
        ch_attente_paiement: 'Attente paiement',
        ch_paiement_effectue: 'Payé',
        ch_valide: 'Validé'
    };

    if (loading) {
        return (
            <Card className="border-2 border-orange-200 bg-orange-50/30">
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-4 w-64" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-20 w-full" />
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="border-2 border-red-200 bg-red-50/30">
                <CardContent className="py-4">
                    <p className="text-red-600 text-sm">{error}</p>
                    <Button variant="ghost" size="sm" onClick={loadPendingCommissions} className="mt-2">
                        <RefreshCw className="w-4 h-4 mr-1" /> Réessayer
                    </Button>
                </CardContent>
            </Card>
        );
    }

    if (prospects.length === 0) {
        // Afficher quand même le widget avec 0 pour rassurer l'admin
        return (
            <Card className="border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50" data-testid="commissions-pending-widget">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                                <Euro className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <CardTitle className="text-lg text-green-800 flex items-center gap-2">
                                    <Euro className="w-5 h-5" />
                                    Commissions à renseigner
                                    <Badge className="bg-green-500 text-white">0</Badge>
                                </CardTitle>
                                <CardDescription className="text-green-600">
                                    Tous les dossiers avancés ont leur commission renseignée
                                </CardDescription>
                            </div>
                        </div>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={loadPendingCommissions}
                            className="text-green-600 hover:text-green-700 hover:bg-green-100"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                </CardHeader>
            </Card>
        );
    }

    return (
        <Card className="border-2 border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50" data-testid="commissions-pending-widget">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                            <CardTitle className="text-lg text-orange-800 flex items-center gap-2">
                                <Euro className="w-5 h-5" />
                                Commissions à renseigner
                                <Badge className="bg-orange-500 text-white">{prospects.length}</Badge>
                            </CardTitle>
                            <CardDescription className="text-orange-600">
                                Dossiers avancés sans commission totale définie
                            </CardDescription>
                        </div>
                    </div>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={loadPendingCommissions}
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-100"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {prospects.map(prospect => (
                        <div 
                            key={prospect.id}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200 hover:border-orange-400 hover:shadow-sm transition-all cursor-pointer"
                            onClick={() => navigate(`/prospects/${prospect.id}`)}
                            data-testid={`pending-commission-${prospect.id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 truncate">
                                    {getProspectName(prospect)}
                                </p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {prospect.needs_ep_commission && prospect.easy_power_stage && (
                                        <Badge className="bg-amber-100 text-amber-700 text-xs">
                                            <Zap className="w-3 h-3 mr-1" />
                                            EP: {STAGE_LABELS[prospect.easy_power_stage] || prospect.easy_power_stage}
                                        </Badge>
                                    )}
                                    {prospect.needs_ch_commission && prospect.chantier_stage && (
                                        <Badge className="bg-blue-100 text-blue-700 text-xs">
                                            <HardHat className="w-3 h-3 mr-1" />
                                            CH: {STAGE_LABELS[prospect.chantier_stage] || prospect.chantier_stage}
                                        </Badge>
                                    )}
                                </div>
                                {(prospect.dossier_apporteur_name || prospect.dossier_vp_name) && (
                                    <p className="text-xs text-slate-500 mt-1 truncate">
                                        {prospect.dossier_apporteur_name && `Apporteur: ${prospect.dossier_apporteur_name}`}
                                        {prospect.dossier_apporteur_name && prospect.dossier_vp_name && ' • '}
                                        {prospect.dossier_vp_name && `VP: ${prospect.dossier_vp_name}`}
                                    </p>
                                )}
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 ml-2" />
                        </div>
                    ))}
                </div>
                
                {prospects.length > 5 && (
                    <p className="text-xs text-orange-600 text-center mt-3">
                        Cliquez sur un dossier pour renseigner la commission totale
                    </p>
                )}
            </CardContent>
        </Card>
    );
}
